import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Formik } from "formik";
import * as Yup from "yup";
import { Alert, Button, Card, Form } from "react-bootstrap";
import Swal from "sweetalert2";

import "./style.scss";
import { ClinicApi } from "../../api";
import { ClinicAccountType } from "../../DTO";
import { Loading } from "../../components";
import { useAppSelector } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user";

type ClinicSettingsValues = {
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    phone: string;
    password: string;
    clinicName: string;
    clinicNumber: string;
    doctorName: string;
};

const validationSchema = Yup.object({
    firstName: Yup.string().trim().required("First name is required."),
    lastName: Yup.string().trim().required("Last name is required."),
    username: Yup.string().trim().required("Username is required."),
    email: Yup.string().trim().email("Enter a valid email.").required("Email is required."),
    phone: Yup.string().trim().required("Phone is required."),
    password: Yup.string().trim(),
    clinicName: Yup.string().trim().required("Clinic name is required."),
    clinicNumber: Yup.string().trim().required("Clinic number is required."),
    doctorName: Yup.string().trim().required("Doctor name is required."),
});

const mapApiErrorsToForm = (errors: Record<string, string[]>) => {
    const formErrors: Record<string, string> = {};

    Object.entries(errors || {}).forEach(([key, value]) => {
        const message = value?.[0];

        if (!message) {
            return;
        }

        if (key === "first_name") {
            formErrors.firstName = message;
        } else if (key === "last_name") {
            formErrors.lastName = message;
        } else if (key === "clinic_name") {
            formErrors.clinicName = message;
        } else if (key === "clinic_number") {
            formErrors.clinicNumber = message;
        } else if (key === "doctor_name") {
            formErrors.doctorName = message;
        } else {
            formErrors[key] = message;
        }
    });

    return formErrors;
};

const toInitialValues = (account?: ClinicAccountType): ClinicSettingsValues => ({
    firstName: account?.firstName || "",
    lastName: account?.lastName || "",
    username: account?.username || "",
    email: account?.email || "",
    phone: account?.phone || "",
    password: "",
    clinicName: account?.clinic?.name || "",
    clinicNumber: account?.clinic?.clinicNumber || "",
    doctorName: account?.clinic?.doctorName || "",
});

export const ClinicSettings = () => {
    const user = useAppSelector(selectUser);
    const [loading, setLoading] = useState(false);
    const [account, setAccount] = useState<ClinicAccountType>();

    useEffect(() => {
        const fetchAccount = async () => {
            try {
                setLoading(true);
                const data = await ClinicApi.ShowClinicAccount(user);
                setAccount(data.account);
            } catch (err: any) {
                Swal.fire({
                    icon: "error",
                    text:
                        err?.data?.message ||
                        err?.message ||
                        "Unable to load clinic account.",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchAccount();
    }, [user]);

    return (
        <div className="my-4 clinic-page clinic-detail-page">
            <Helmet>
                <title>D2Type | Clinic Settings</title>
            </Helmet>

            {loading && <Loading />}

            <div className="clinic-detail-shell">
                <div className="clinic-detail-header">
                    <div>
                        <h2 className="clinic-snapshot-title">Clinic Account Settings</h2>
                        <p className="clinic-snapshot-subtitle">
                            Update clinic identity, doctor details, and account login information.
                        </p>
                    </div>
                </div>

                <Card className="clinic-surface border-0">
                    <Card.Body className="clinic-panel">
                        <Formik<ClinicSettingsValues>
                            enableReinitialize
                            initialValues={toInitialValues(account)}
                            validationSchema={validationSchema}
                            onSubmit={async (values, { setErrors, setStatus }) => {
                                try {
                                    setLoading(true);
                                    setStatus(undefined);

                                    const response = await ClinicApi.UpdateClinicAccount(user, {
                                        first_name: values.firstName.trim(),
                                        last_name: values.lastName.trim(),
                                        username: values.username.trim(),
                                        email: values.email.trim(),
                                        phone: values.phone.trim(),
                                        password: values.password.trim() || undefined,
                                        clinic_name: values.clinicName.trim(),
                                        clinic_number: values.clinicNumber.trim(),
                                        doctor_name: values.doctorName.trim(),
                                    });

                                    setAccount(response.account);
                                    Swal.fire({
                                        icon: "success",
                                        text: response.message,
                                    });
                                } catch (err: any) {
                                    const apiErrors = err?.data?.errors;

                                    if (apiErrors) {
                                        setErrors(mapApiErrorsToForm(apiErrors));
                                        setStatus(
                                            err?.data?.message ||
                                                "Please review the highlighted fields.",
                                        );
                                    } else {
                                        Swal.fire({
                                            icon: "error",
                                            text:
                                                err?.data?.message ||
                                                err?.message ||
                                                "Unable to update clinic account.",
                                        });
                                    }
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        >
                            {({
                                handleSubmit,
                                handleChange,
                                handleBlur,
                                values,
                                touched,
                                errors,
                                isSubmitting,
                                status,
                            }) => (
                                <Form onSubmit={handleSubmit}>
                                    {status && <Alert variant="danger">{status}</Alert>}

                                    <div className="clinic-settings-grid">
                                        <section className="clinic-settings-section">
                                            <h3>Clinic</h3>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Clinic name</Form.Label>
                                                <Form.Control
                                                    name="clinicName"
                                                    value={values.clinicName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isInvalid={touched.clinicName && !!errors.clinicName}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.clinicName}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Clinic number</Form.Label>
                                                <Form.Control
                                                    name="clinicNumber"
                                                    value={values.clinicNumber}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isInvalid={touched.clinicNumber && !!errors.clinicNumber}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.clinicNumber}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group>
                                                <Form.Label>Doctor name</Form.Label>
                                                <Form.Control
                                                    name="doctorName"
                                                    value={values.doctorName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isInvalid={touched.doctorName && !!errors.doctorName}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.doctorName}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </section>

                                        <section className="clinic-settings-section">
                                            <h3>Admin account</h3>
                                            <div className="row g-3">
                                                <div className="col-12 col-md-6">
                                                    <Form.Label>First name</Form.Label>
                                                    <Form.Control
                                                        name="firstName"
                                                        value={values.firstName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.firstName && !!errors.firstName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.firstName}
                                                    </Form.Control.Feedback>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <Form.Label>Last name</Form.Label>
                                                    <Form.Control
                                                        name="lastName"
                                                        value={values.lastName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.lastName && !!errors.lastName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.lastName}
                                                    </Form.Control.Feedback>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <Form.Label>Username</Form.Label>
                                                    <Form.Control
                                                        name="username"
                                                        value={values.username}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.username && !!errors.username}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.username}
                                                    </Form.Control.Feedback>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <Form.Label>Email</Form.Label>
                                                    <Form.Control
                                                        name="email"
                                                        value={values.email}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.email && !!errors.email}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.email}
                                                    </Form.Control.Feedback>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <Form.Label>Phone</Form.Label>
                                                    <Form.Control
                                                        name="phone"
                                                        value={values.phone}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.phone && !!errors.phone}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.phone}
                                                    </Form.Control.Feedback>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <Form.Label>New password</Form.Label>
                                                    <Form.Control
                                                        name="password"
                                                        type="password"
                                                        value={values.password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isInvalid={touched.password && !!errors.password}
                                                        placeholder="Leave blank to keep current password"
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.password}
                                                    </Form.Control.Feedback>
                                                </div>
                                            </div>
                                        </section>
                                    </div>

                                    <div className="d-flex justify-content-end mt-4">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={loading || isSubmitting}
                                        >
                                            Save settings
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
};
